import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';

const MODEL =
  'https://cdn.jsdelivr.net/gh/bilalaurangzebkhan/macbook-animation-reactjs@main/public/mac.glb';

type Macbook3DProps = {
  screenSrc: string;
  screenAlt?: string;
  scrollFrom?: number;
  scrollTo?: number;
  style?: React.CSSProperties;
  className?: string;
};

const Macbook3D: React.FC<Macbook3DProps> = ({
  screenSrc,
  screenAlt = '',
  scrollFrom = 0.34,
  scrollTo = 0.62,
  style,
  className,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || fallback) return;

    let disposed = false;
    let raf = 0;
    let renderer: THREE.WebGLRenderer | null = null;
    let ro: ResizeObserver | null = null;
    let onMove: ((e: PointerEvent) => void) | null = null;

    const boot = async () => {
      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'display:block; width:100%; height:100%;';
      host.appendChild(canvas);

      renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      });
      renderer.setPixelRatio(Math.min(Math.max(window.devicePixelRatio, 2), 3));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.15;

      const scene = new THREE.Scene();
      const pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      (scene as THREE.Scene & { environmentIntensity?: number }).environmentIntensity = 0.75;

      const camera = new THREE.PerspectiveCamera(12, 1, 1, 1000);
      camera.position.set(0, -6, 142);
      camera.lookAt(0, -4.6, 0);

      scene.add(new THREE.HemisphereLight(0xffffff, 0xd8dee8, 0.9));
      const key = new THREE.DirectionalLight(0xffffff, 1.6);
      key.position.set(-40, 60, 80);
      scene.add(key);

      const draco = new DRACOLoader();
      draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
      const loader = new GLTFLoader().setDRACOLoader(draco);
      const gltf: any = await new Promise((res, rej) =>
        loader.load(MODEL, res, undefined, rej)
      );
      if (disposed) return;

      const meshes: Record<string, THREE.Object3D> = {};
      gltf.scene.traverse((o: THREE.Object3D) => {
        meshes[o.name] = o;
      });

      const lid = meshes.screen as THREE.Object3D | undefined;
      if (lid) lid.rotation.x = THREE.MathUtils.degToRad(180);

      const matte = meshes.matte as THREE.Mesh | undefined;
      if (matte && screenSrc) {
        const tex = await new THREE.TextureLoader().loadAsync(screenSrc);
        if (disposed) return;
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.center.set(0.5, 0.5);
        tex.flipY = true;
        tex.anisotropy = renderer!.capabilities.getMaxAnisotropy();
        tex.generateMipmaps = true;
        tex.minFilter = THREE.LinearMipmapLinearFilter;
        matte.material = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false });
      }

      const group = new THREE.Group();
      group.position.set(0, -14, 20);
      group.add(gltf.scene);
      scene.add(group);

      const resize = () => {
        if (!renderer || !host) return;
        const w = host.clientWidth || 1;
        const h = host.clientHeight || 1;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.fov = 12 * Math.max(1, 1.9 / (w / h));
        camera.updateProjectionMatrix();
      };
      resize();
      ro = new ResizeObserver(resize);
      ro.observe(host);

      let mx = 0;
      let px = 0;
      onMove = (e: PointerEvent) => {
        const r = host.getBoundingClientRect();
        if (!r.width) return;
        mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      };
      window.addEventListener('pointermove', onMove, { passive: true });

      const ease = (t: number) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);
      const track = host.closest('section') || host.parentElement;
      let open = 0;

      const tick = () => {
        if (disposed || !renderer) return;
        raf = requestAnimationFrame(tick);
        let target = 0;
        if (track) {
          const r = track.getBoundingClientRect();
          const span = Math.max(r.height - window.innerHeight, 1);
          const p = clamp01(-r.top / span);
          target = clamp01((p - scrollFrom) / Math.max(scrollTo - scrollFrom, 0.01));
        }
        open += (target - open) * 0.18;
        if (lid) lid.rotation.x = THREE.MathUtils.degToRad(180 - ease(open) * 88);
        px += (mx - px) * 0.05;
        group.rotation.y = px * 0.25;
        renderer.render(scene, camera);
      };
      tick();
    };

    boot().catch(() => {
      if (!disposed) setFallback(true);
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      ro?.disconnect();
      if (onMove) window.removeEventListener('pointermove', onMove);
      renderer?.dispose();
      while (host.firstChild) host.removeChild(host.firstChild);
    };
  }, [screenSrc, scrollFrom, scrollTo, fallback]);

  if (fallback) {
    return (
      <div className={className} style={{ display: 'block', position: 'relative', width: '100%', height: '100%', ...style }}>
        <img
          src={screenSrc}
          alt={screenAlt}
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            margin: 'auto',
            borderRadius: 14,
            border: '1px solid #EDF0F5',
          }}
        />
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      className={className}
      style={{ display: 'block', position: 'relative', width: '100%', height: '100%', ...style }}
    />
  );
};

export default Macbook3D;
